import { PaginationBody, PaginationBodyUsers } from '@Entities/pagination'
import { RecordingGroupStatus } from '@Components/MatchComponents/v2/recording-group-status'
import {
    base,
    paginationPath,
    paginationPath2v,
    paginationSearchPath,
    portalBase2v,
    publicBase2v,
    rightsholderBase2v,
} from '@App/dicts/pathes'
import * as statementsPaths from '@App/dicts/rightsholder-statements-paths'
import * as overclaimPaths from '@App/dicts/rightsholder-overclaim-paths'
import * as catalogTransferPaths from '@App/dicts/rightsholder-catalog-transfer-paths'
import { CatalogTransferSearchQuery } from '@App/stores/search/models/catalog-transfer-search'

export class RightsHolder {
    rightsHolders = base('rightsholders')
    memberList = portalBase2v('members')
    createRightsHolder = portalBase2v('rightsholder')
    newRightsholderQuestionnaire = portalBase2v('questions')

    getRelatedRightsholders = () => portalBase2v(`rightsholder/related`)
    verifyRightsholder = (hfaPublisherNumber: string) => portalBase2v(`rightsholder/verify/${hfaPublisherNumber}`)
    editRightsHolder = (rightsholderId: number) => rightsholderBase2v(rightsholderId)
    rightsHolderSummary = (rightsholderId: number) => rightsholderBase2v(rightsholderId, 'summary')
    rightsholderContact = (rightsholderId: number) => rightsholderBase2v(rightsholderId, 'contact')
    rightsholderContacts = (rightsholderId: number) => rightsholderBase2v(rightsholderId, 'contacts')
    rightsholderContactById = (rightsholderId: number, contactId: string) =>
        rightsholderBase2v(rightsholderId, `contact/${contactId}`)
    rightsHolderUsers = (rightsholderId: number, pagination?: Partial<PaginationBodyUsers>) => {
        const url = new URL(rightsholderBase2v(rightsholderId, `users${paginationPath2v(pagination)}`))
        if (pagination?.group) {
            url.searchParams.append('group', pagination.group)
        }
        return url.toString()
    }
    rightsHolderUser = (rightsholderId: number, userId: string) => rightsholderBase2v(rightsholderId, `user/${userId}`)
    actionRightsHolderUser = (rightsholderId: number) => rightsholderBase2v(rightsholderId, 'user')
    tipaltiIframeUrl = (rightsholderId: number, payeeIpId: number) =>
        rightsholderBase2v(rightsholderId, `payees/${payeeIpId}/iframe`)
    claimedShares = (rightsholderId: number) => base(`rightsholder/${rightsholderId}/works/shares`)

    // statements "pathes"
    dspRates = statementsPaths.dspRates
    ratePeriods = statementsPaths.ratePeriods
    rateDsps = statementsPaths.rateDsps
    rateSheetDownload = statementsPaths.rateSheetDownload
    royalties = statementsPaths.royalties
    payeeActivityDownload = statementsPaths.payeeActivityDownload
    latestRoyalties = statementsPaths.latestRoyalties
    allPayees = statementsPaths.allPayees
    statements = statementsPaths.statements
    tsvRequest = statementsPaths.tsvRequest
    annualTSVRequest = statementsPaths.annualTSVRequest
    distributionSummary = statementsPaths.distributionSummary

    // processing analytics
    paymentsYTD = statementsPaths.paymentsYTD
    paymentsLastDistribution = statementsPaths.paymentsLastDistribution
    paymentsForDistributions = statementsPaths.paymentsForDistributions

    // unmatched "pathes"
    unclaimedSearch = (pagination: PaginationBody) =>
        base(`search/matching${paginationSearchPath(pagination)}&unclaimed=true`)
    unmatchedRightsholderSearch = (rightsholderId: number, queryString: string) =>
        rightsholderBase2v(rightsholderId, `search/unmatched-recordings?${queryString}`)
    dspRecordingDetails = (recordingId: string) => base(`dsp-recording/${recordingId}`)
    dspRecordingsForSongCode = (songCode: string, pagination: PaginationBody, order = 'matchedAmount') =>
        base(`dsp-recording/matched/${songCode}${paginationPath(pagination)}&order=${order}&direction=desc`)
    publicRecordingsForSongCode = (songCode: string, pagination: PaginationBody, order = 'matchedAmount') =>
        publicBase2v(`work/${songCode}/recording${paginationPath2v(pagination)}&order=${order}&direction=desc`)
    createMatchSuggestion = (rightsholderId: number) =>
        base(`dsp-recording/rightsholder/${rightsholderId}/suggested-matches`)
    matchSuggestions = (rightsholderId: number, statuses: string[], pagination?: PaginationBody) => {
        const url = new URL(rightsholderBase2v(rightsholderId, `matching/suggestions${paginationPath2v(pagination)}`))
        url.searchParams.append('sort', 'submission_timestamp' + ',desc')
        statuses.forEach((status: string) => url.searchParams.append('status', status))
        return url.toString()
    }
    singleMatchSuggestion = (rightsholderId: number, suggestionId: number) =>
        rightsholderBase2v(rightsholderId, `matching/suggestions/${suggestionId}`)
    recordingGroupDetails = (
        rightsholderId: number,
        suggestionId: number,
        recordingGroupStatus: RecordingGroupStatus[],
        pagination?: PaginationBody
    ) => {
        const url = new URL(
            rightsholderBase2v(
                rightsholderId,
                `matching/suggestions/${suggestionId}/recording_groups${paginationPath2v(pagination)}`
            )
        )
        recordingGroupStatus.forEach((thisStatus) => url.searchParams.append('status', thisStatus))
        return url.toString()
    }
    matchSummary = (rightsholderId: number) => rightsholderBase2v(rightsholderId, `matching/summary`)

    // Overclaim "pathes"
    overclaimViewByHeaderId = overclaimPaths.overclaimViewByHeaderId
    overclaimHistoryByHeaderId = overclaimPaths.overclaimHistoryByHeaderId
    overclaimStatusCounts = overclaimPaths.overclaimStatusCounts
    sendOverclaimEmail = overclaimPaths.sendOverclaimEmail
    renderOverclaimEmailTemplate = overclaimPaths.renderOverclaimEmailTemplate
    amendOverclaimShares = overclaimPaths.amendOverclaimShares
    bulkAmendOverclaimShares = overclaimPaths.bulkAmendOverclaimShares
    createRelinquishment = overclaimPaths.createRelinquishment
    createBulkRelinquishment = overclaimPaths.createBulkRelinquishment
    getRelinquishments = overclaimPaths.getRelinquishments
    uploadDocumentForOverclaim = overclaimPaths.uploadDocumentForOverclaim
    finalizeDocumentUpload = overclaimPaths.finalizeDocumentUpload
    bulkUploadDocumentForOverclaims = overclaimPaths.bulkUploadDocumentForOverclaims
    updateOverclaimDocumentDescription = overclaimPaths.updateOverclaimDocumentDescription
    getOverclaimUploadDocumentDownload = overclaimPaths.getOverclaimUploadDocumentDownload
    searchOverclaimDocuments = overclaimPaths.searchOverclaimDocuments
    attachExistingOverclaimDocument = overclaimPaths.attachExistingOverclaimDocument

    // Catalog Transfer paths
    createCatalogTransfer = catalogTransferPaths.createCatalogTransfer
    getCatalogTransfer = catalogTransferPaths.getCatalogTransfer
    getCatalogTransferList = (
        rightsholderId: number,
        searchQuery: CatalogTransferSearchQuery,
        pagination: PaginationBody
    ) => catalogTransferPaths.getCatalogTransferList(rightsholderId, searchQuery, pagination)
    getCatalogTransferReviewList = (
        rightsholderId: number,
        searchQuery: CatalogTransferSearchQuery,
        pagination: PaginationBody
    ) => catalogTransferPaths.getCatalogTransferReviewList(rightsholderId, searchQuery, pagination)
    getSongwriterSubmissionSuggestions = catalogTransferPaths.getSongwriterSubmissionSuggestions
    updateSongwriterSubmissionSuggestionStatus = catalogTransferPaths.updateSongwriterSubmissionSuggestionStatus
    updateCatalogTransfer = catalogTransferPaths.updateCatalogTransfer
    submitCatalogTransfer = catalogTransferPaths.submitCatalogTransfer
    catalogTransferDecision = catalogTransferPaths.catalogTransferDecision
    linkCatalogTransferPublisher = catalogTransferPaths.linkCatalogTransferPublisher
    uploadCatalogTransferDocument = catalogTransferPaths.uploadCatalogTransferDocument
    downloadCatalogTransferDocument = catalogTransferPaths.downloadCatalogTransferDocument
    importCatalogTransferFile = catalogTransferPaths.importCatalogTransferFile
    importCatalogTransferFileStreaming = catalogTransferPaths.importCatalogTransferFileStreaming
    exportCatalogTransferFile = catalogTransferPaths.exportCatalogTransferFile
    workMerge = (rightsholderId: number) => rightsholderBase2v(rightsholderId, `workMerge`)
    workMergeReview = (rightsholderId: number) => rightsholderBase2v(rightsholderId, `workMerge/review`)
    workMergeRequest = (rightsholderId: number, workMergeRequestId: number) =>
        rightsholderBase2v(rightsholderId, `workMerge/${workMergeRequestId}`)
    workMergeReviewRequest = (rightsholderId: number, workMergeRequestId: number) =>
        rightsholderBase2v(rightsholderId, `workMerge/review/${workMergeRequestId}`)
    workMergeRequestWork = (rightsholderId: number, workMergeRequestId: number, workId: number) =>
        rightsholderBase2v(rightsholderId, `workMerge/${workMergeRequestId}/work/${workId}`)
    diagnosticDashboard = (rightsholderId: number) => rightsholderBase2v(rightsholderId, 'dashboard/diagnostic')
}
